import { orderStatus } from '@/theme/status';
import type { EstadoOrden, MotivoCancelacion } from '@/types/domain';
import type { GuideSendResult, Order, OrderItem, OrderStatusHistoryEntry } from '@/types/orders';

/**
 * Formato de las órdenes para la pantalla (módulo 7).
 *
 * El negocio NO tiene moneda configurada (ni en `Settings` ni en `Order`), así que aquí no se
 * inventa un símbolo: los importes se pintan como números en formato es-CO (miles con punto) y
 * cada columna dice qué son ("total", "precio unitario"). El bot habla del dinero en la
 * conversación; la pantalla solo comprueba que los números sean los correctos.
 *
 * Aquí viven también las dos reglas del contrato que la UI necesita para no ofrecer acciones
 * imposibles: las TRANSICIONES de estado y los estados que aceptan la guía. Son espejo de
 * `orders.service.ts` (`TRANSICIONES` y `ESTADOS_PARA_GUIA`): el backend sigue validando (responde
 * 400/409), esto solo evita mostrar un botón que va a fallar.
 */

const LOCALE = 'es-CO';

/** "25.000" / "22.500,5" (es-CO). */
export function formatNumber(value: number): string {
  return value.toLocaleString(LOCALE, { maximumFractionDigits: 2 });
}

/** "3 órdenes" / "1 orden" (singular y plural explícitos: no hay pluralización automática en es). */
export function countLabel(count: number, singular: string, plural: string): string {
  return `${formatNumber(count)} ${count === 1 ? singular : plural}`;
}

/**
 * Transiciones permitidas por estado — espejo de `TRANSICIONES`
 * (`backend/src/modules/orders/orders.service.ts`). `ENTREGADA` y `CANCELADA` son finales.
 */
export const ORDER_TRANSITIONS: Record<EstadoOrden, EstadoOrden[]> = {
  PENDIENTE_CONFIRMACION: ['CONFIRMADA', 'CANCELADA'],
  CONFIRMADA: ['EN_PREPARACION', 'CANCELADA'],
  EN_PREPARACION: ['ENVIADA', 'CANCELADA'],
  ENVIADA: ['ENTREGADA', 'CANCELADA'],
  ENTREGADA: [],
  CANCELADA: [],
};

/** Estados a los que se puede mover la orden ahora mismo (vacío = estado final). */
export function nextEstados(estado: EstadoOrden): EstadoOrden[] {
  return ORDER_TRANSITIONS[estado] ?? [];
}

/** ¿La orden ya cerró? (no se puede mover ni cargar guía). */
export function isFinalEstado(estado: EstadoOrden): boolean {
  return nextEstados(estado).length === 0;
}

/**
 * Estados que aceptan la guía de envío — espejo de `ESTADOS_PARA_GUIA`.
 *
 * `PENDIENTE_CONFIRMACION` queda afuera (primero hay que confirmar la venta) y `ENTREGADA` /
 * `CANCELADA` también (el pedido ya cerró). `ENVIADA` se mantiene para re-subir la guía.
 */
export const GUIDE_ESTADOS: EstadoOrden[] = ['CONFIRMADA', 'EN_PREPARACION', 'ENVIADA'];

/** ¿Se puede cargar (o reemplazar) la guía con la orden en este estado? */
export function canAttachGuide(estado: EstadoOrden): boolean {
  return GUIDE_ESTADOS.includes(estado);
}

/** "Color: Negro · Talla: M" — las variantes elegidas, en el orden en que las guardó el backend. */
export function describeVariants(variantes: OrderItem['variantes']): string {
  if (!variantes || typeof variantes !== 'object') return '';

  return Object.entries(variantes)
    .filter(([, value]) => value !== null && value !== undefined && String(value).trim().length > 0)
    .map(([key, value]) => `${key}: ${String(value)}`)
    .join(' · ');
}

/** "2 × Camisa lino (Color: Negro · Obsequio: gorra)" — una línea del pedido. */
export function describeOrderItem(item: OrderItem): string {
  const detalles: string[] = [];

  const variantes = describeVariants(item.variantes);
  if (variantes) detalles.push(variantes);
  if (item.obsequio?.trim()) detalles.push(`Obsequio: ${item.obsequio.trim()}`);

  const sufijo = detalles.length > 0 ? ` (${detalles.join(' · ')})` : '';
  return `${formatNumber(item.cantidad)} × ${item.nombre}${sufijo}`;
}

/**
 * Cuántos productos y cuántas unidades lleva la orden: "2 productos · 3 unidades".
 *
 * El ítem se guarda con su cantidad, así que el número de unidades (lo que se empaca) es distinto
 * del número de líneas del pedido: la tabla muestra los dos para poder cuadrar el despacho.
 */
export function orderItemsSummary(order: { productos: OrderItem[] }): string {
  const items = Array.isArray(order.productos) ? order.productos : [];
  if (items.length === 0) return 'Sin productos';

  const unidades = items.reduce(
    (sum, item) => sum + (typeof item.cantidad === 'number' ? item.cantidad : 0),
    0,
  );

  return `${countLabel(items.length, 'producto', 'productos')} · ${countLabel(unidades, 'unidad', 'unidades')}`;
}

/**
 * Resumen de la tarjeta: "12 órdenes · 4 en curso · 350.000 en total (sin canceladas)".
 *
 * "En curso" son los estados no finales (`PENDIENTE_CONFIRMACION`…`ENVIADA`): justo los pedidos
 * que hay que trabajar. El importe excluye las canceladas para que la cifra no engañe.
 */
export function summarizeOrders(orders: Order[]): string {
  if (orders.length === 0) return 'Sin órdenes';

  const enCurso = orders.filter((order) => !isFinalEstado(order.estado)).length;
  const importe = orders
    .filter((order) => order.estado !== 'CANCELADA')
    .reduce((sum, order) => sum + order.total, 0);

  return `${countLabel(orders.length, 'orden', 'órdenes')} · ${formatNumber(enCurso)} en curso · ${formatNumber(importe)} en total (sin canceladas)`;
}

/**
 * Todo el texto por el que se puede encontrar una orden, en minúsculas.
 *
 * El endpoint NO tiene búsqueda (solo `estado`, `conversationId` y `limit`), así que el buscador
 * de la pantalla filtra lo que ya está en caché. Se incluye lo que el usuario tiene en la cabeza
 * cuando busca ("la de Ana", "la de Cali", "GUIA-12345", el teléfono, el producto), no solo el
 * nombre del cliente.
 */
export function clientSearchText(order: Order): string {
  const items = Array.isArray(order.productos) ? order.productos : [];

  return [
    order.clienteNombre,
    order.telefono ?? '',
    order.ciudad ?? '',
    order.departamento ?? '',
    order.barrio ?? '',
    order.direccion ?? '',
    order.metodoPago ?? '',
    order.guiaEnvioNumero ?? '',
    order.id,
    order.conversationId ?? '',
    ...items.map(describeOrderItem),
  ]
    .join(' ')
    .toLowerCase();
}

/**
 * Qué decirle al admin sobre el aviso que se le mandó al cliente al cargar la guía.
 *
 * El backend devuelve un `motivo` técnico (`GuideSendResult`) porque hay muchos casos en los que la
 * guía se guarda bien pero el aviso no sale (sin URL pública, sin conversación, Meta falló). La
 * pantalla lo traduce: el admin tiene que saber si el cliente ya recibió la foto o si hay que
 * reintentar.
 */
export function describeGuideSend(envio: GuideSendResult): string {
  if (envio.enviado) {
    return 'El cliente ya recibió la foto de la guía con el número de envío.';
  }

  switch (envio.motivo) {
    case 'plantilla':
      return 'El aviso salió por la plantilla PEDIDO_ENVIADO (la ventana de 24 h estaba cerrada).';
    case 'foto_no_publica':
      return 'La guía quedó guardada, pero no se pudo enviar la foto: su URL no es pública (falta PUBLIC_BASE_URL en el backend). Se le mandó solo el texto.';
    case 'sin_foto':
      return 'La orden no tenía foto de guía que enviar.';
    case 'envio_fallido':
      return 'No se pudo avisar al cliente: el envío por el canal falló. La guía quedó guardada, así que puedes reemplazarla para reintentar.';
    case 'sin_conversacion':
      return 'La orden no nació de un chat, así que no hay a quién avisarle. La guía quedó guardada.';
    default:
      return 'No se pudo enviar el aviso al cliente y el backend no reportó un motivo conocido.';
  }
}

/**
 * "Creada como Confirmada" / "Pendiente de confirmación → Confirmada".
 *
 * La primera fila del historial no tiene `estadoAnterior` (es el nacimiento de la orden), así que
 * se lee distinto que un cambio de estado.
 */
/**
 * Por qué se puede cancelar una orden (3-oct-2026). Espejo del enum `MotivoCancelacion`.
 *
 * Los motivos van ordenados de más a menos frecuente en una tienda: primero los que de verdad se usan
 * todos los días (el cliente se arrepiente), y «orden de prueba» se queda al final aunque para quien
 * está probando el sistema sea el más importante, porque para el negocio es una excepción.
 */
export const motivoCancelacionMeta: Record<MotivoCancelacion, string> = {
  CLIENTE: 'El cliente cancela',
  COBRO: 'No se pudo cobrar',
  NEGOCIO: 'El negocio no puede cumplir (sin stock, etc.)',
  PRUEBA: 'Orden de prueba (no es una venta real)',
  OTRO: 'Otro motivo',
};

/** Rótulo del motivo elegido, o `null` si la orden no está cancelada. */
export function motivoCancelacionLabel(motivo: MotivoCancelacion | null | undefined): string | null {
  return motivo ? motivoCancelacionMeta[motivo] : null;
}

export function describeHistoryEntry(entry: OrderStatusHistoryEntry): string {
  const hacia = orderStatus[entry.estadoNuevo].label;
  if (!entry.estadoAnterior) return `Creada como ${hacia}`;

  return `${orderStatus[entry.estadoAnterior].label} → ${hacia}`;
}

/** Líneas de la dirección de entrega, sin repetir vacíos ("Calle 10 # 4-11", "Granada", "Cali"). */
export function orderAddressLines(order: Order): string[] {
  const ciudadDepartamento = [order.ciudad, order.departamento].filter(Boolean).join(', ');

  return [order.direccion, order.barrio, ciudadDepartamento]
    .map((line) => (line ?? '').trim())
    .filter((line) => line.length > 0);
}


/* ------------------------- Envío a Dropi (docs/DROPI.md) ------------------- */

/**
 * Qué campo falló al enviar la orden a Dropi (`orders.dropi_error_campo`), en palabras.
 *
 * El backend guarda el campo **y** la nota accionable en la orden; acá solo se traduce la etiqueta
 * para que el admin vea de un vistazo DÓNDE tiene que ir a corregir: si fue la ciudad, la bodega del
 * proveedor, un producto sin vincular…
 */
export const campoFalloDropiLabel: Record<string, string> = {
  ciudad_departamento: 'Ciudad y departamento',
  bodega: 'Bodega del proveedor',
  productos: 'Productos sin vincular',
  direccion: 'Dirección',
  telefono: 'Teléfono',
  conexion: 'Conexión con Dropi',
  otro: 'Otro',
};

/** Etiqueta del campo del fallo. `null` cuando la orden no tiene nota guardada. */
export function campoFalloDropi(campo: string | null | undefined): string | null {
  if (!campo) return null;
  return campoFalloDropiLabel[campo] ?? 'Revisá el envío';
}

/**
 * Qué hizo la última sincronización con Dropi, en una línea (la pinta el diálogo de envío).
 *
 * `avanzado` trae los estados por los que pasó NUESTRA orden (el espejo camina la cadena, no salta),
 * así que el admin ve «Pendiente de confirmación → Confirmada → En preparación».
 */
export function describeEspejoDropi(resultado: {
  dropiEstado: string | null;
  avanzado: string[];
  guiaNueva: string | null;
  aviso: string | null;
}): string {
  const partes: string[] = [];

  if (resultado.dropiEstado) partes.push(`Dropi dice «${resultado.dropiEstado}»`);
  if (resultado.avanzado.length > 0) {
    partes.push(`la orden pasó por ${resultado.avanzado.join(' → ')}`);
  } else {
    partes.push('no hubo estados que avanzar');
  }
  if (resultado.guiaNueva) partes.push(`guía ${resultado.guiaNueva} guardada`);

  const textos: Record<string, string> = {
    guia: 'se le avisó al cliente con la guía',
    estado: 'se le avisó al cliente del avance',
    sin_conversacion: 'la orden no nació de un chat: no se pudo avisar al cliente',
  };
  if (resultado.aviso && textos[resultado.aviso]) partes.push(textos[resultado.aviso]);

  return `${partes.join(' · ')}.`;
}

